// @flow
import React from 'react';
import { compose } from 'redux';

import { addTranslationKeys } from 'Src/i18n';
import type { GQLProps, CMSProps } from 'Client/cms/toolkit/product-tags/productTags.resolver';
import { getProductProps, setProductHref } from 'Components/widgets/product/Product.mapper';

import DynamicLink from 'Components/frameworks/dynamic-link/DynamicLink';
import { LineTitle } from 'Components/widgets/line-title/LineTitle';
import { ProductTags } from 'Components/widgets/product-tags/ProductTags';
import { SiteContainer } from 'Components/frameworks/site-container/SiteContainer';
import { TrackClick } from 'Tracking/track-click/TrackClick';

export type LineProps = {
  ...$Exact<CMSProps>,
  ...$Exact<GQLProps>,
};

export const ProductTagsLine = ({
  config,
  id,
  products,
  seeAllProductsLink,
  tags,
  title,
}: LineProps) => {
  if (!tags || tags.length === 0 || !products || products.length === 0) {
    return null;
  }

  addTranslationKeys(config);

  const formattedProducts = products.map(product =>
    compose(
      getProductProps,
      setProductHref,
    )({ config, product, isSliderDisabled: true }),
  );

  const tagsWithProducts = tags
    .map(tag => {
      const productsInTag = tag.productIds
        .map(productId => formattedProducts.find(p => p.id === productId))
        .filter(Boolean);
      return productsInTag.length > 0
        ? {
            ...tag,
            products: productsInTag,
          }
        : null;
    })
    .filter(Boolean);

  const trackingData = {
    actionGA: 'push_content_IdealResort',
  };

  return (
    tagsWithProducts.length > 0 && (
      <SiteContainer className="Line" dataTestId="Line-ProductTags" id={id}>
        <div className="flex flex-wrap justify-between">
          <LineTitle className="flex-1 md:flex-none">{title}</LineTitle>
          <TrackClick
            as={DynamicLink}
            className="m-auto md:mx-0 mt-4 md:mt-1 order-last md:order-none text-base text-gray-darker hover:text-gray-active focus:text-gray-active underline"
            trackingData={{ ...trackingData, labelGA: 'seeAllResorts' }}
            url={seeAllProductsLink.url}
          >
            {seeAllProductsLink.label}
          </TrackClick>
          <ProductTags tags={tagsWithProducts} trackingData={trackingData} />
        </div>
      </SiteContainer>
    )
  );
};
